import React, { useState } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Button,
  Input,
  Dropdown,
  Option,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Text,
  ToggleButton,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Label,
} from '@fluentui/react-components';
import {
  Edit24Regular,
  Delete24Regular,
  Wrench24Regular,
  Filter24Regular,
  QuestionCircle20Regular,
  Add20Regular,
} from '@fluentui/react-icons';
import maintenanceReminders from '../mocks/maintenance-reminders.json';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#edf1f5',
    ...shorthands.padding('16px'),
  },
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '16px',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.padding('12px', '16px'),
    ...shorthands.borderRadius('8px'),
    boxShadow: tokens.shadow8,
  },
  filterGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  filterLabel: {
    fontSize: '14px',
    fontWeight: '600',
    minWidth: 'fit-content',
  },
  deviceToggleGroup: {
    display: 'flex',
    gap: '0px',
  },
  unitsToggleGroup: {
    display: 'flex',
    gap: '0px',
  },
  tableContainer: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    boxShadow: tokens.shadow8,
    ...shorthands.overflow('hidden'),
  },
  tableHeader: {
    backgroundColor: '#3d3d3d',
    color: '#ffffff',
  },
  tableHeaderRow: {
    backgroundColor: '#3d3d3d',
  },
  tableHeaderCell: {
    color: '#ffffff',
    fontWeight: '600',
    fontSize: '14px',
  },
  filterSearch: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  completeButton: {
    backgroundColor: '#ff8c00',
    color: '#ffffff',
    fontWeight: '600',
    '&:hover': {
      backgroundColor: '#ff7700',
    },
  },
  actionCell: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  serviceIcon: {
    marginRight: '8px',
  },
});

interface MaintenanceReminder {
  id: string;
  trackerItem: string;
  serviceType: string;
  serviceInterval: string;
  nextServiceDue: string;
}

//Fetch mock data in dev. Switch to real data in prod
const reminders: MaintenanceReminder[] = maintenanceReminders;

export default function MaintenanceActiveReminders() {
  const styles = useStyles();
  const [groupFilter, setGroupFilter] = useState('Select group');
  const [serviceTypeFilter, setServiceTypeFilter] = useState('Select service type');
  const [deviceFilter, setDeviceFilter] = useState('Select device');
  const [unitsFilter, setUnitsFilter] = useState('Select units');
  const [searchFilter, setSearchFilter] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState<MaintenanceReminder | null>(null);

  // Export function
  const exportToCSV = () => {
    const headers = ['Tracker/Item', 'Service Type', 'Service Interval', 'Next Service Due'];
    const rows = reminders.map(reminder => [
      reminder.trackerItem,
      reminder.serviceType,
      reminder.serviceInterval,
      reminder.nextServiceDue,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `active_reminders_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Listen for export event
  React.useEffect(() => {
    const handleExport = (event: CustomEvent) => {
      if (event.detail === 'active') {
        exportToCSV();
      }
    };

    window.addEventListener('maintenanceExport', handleExport as EventListener);
    return () => {
      window.removeEventListener('maintenanceExport', handleExport as EventListener);
    };
  }, []);

  // Edit form state
  const [editTracker, setEditTracker] = useState('');
  const [editServiceType, setEditServiceType] = useState('');
  const [editReminderType, setEditReminderType] = useState('');
  const [editCurrentOdometer, setEditCurrentOdometer] = useState('');
  const [editInterval, setEditInterval] = useState('');
  const [editKmUntilNextDue, setEditKmUntilNextDue] = useState('');
  const [editReminderTiming, setEditReminderTiming] = useState('');
  const [editEmailNotifications, setEditEmailNotifications] = useState('');
  const [editTextNotifications, setEditTextNotifications] = useState('');

  const handleEdit = (reminder: MaintenanceReminder) => {
    setSelectedReminder(reminder);
    setEditTracker(reminder.trackerItem);
    setEditServiceType(reminder.serviceType);
    setEditReminderType('Kilometer Based');
    setEditCurrentOdometer('1110785');
    setEditInterval('8047');
    setEditKmUntilNextDue('6635');
    setEditReminderTiming('402');
    setEditEmailNotifications('');
    setEditTextNotifications('');
    setEditDialogOpen(true);
  };

  return (
    <div className={styles.container}>
      {/* Filter Bar */}
      <div className={styles.filterBar}>
        <div className={styles.filterGroup}>
          <Dropdown
            value={groupFilter}
            onOptionSelect={(_, data) => setGroupFilter(data.optionText || 'All')}
            style={{ minWidth: '150px' }}
          >
            <Option>All</Option>
            <Option>Fleet A</Option>
            <Option>Fleet B</Option>
          </Dropdown>
        </div>

        <div className={styles.filterGroup}>
          <Dropdown
            value={serviceTypeFilter}
            onOptionSelect={(_, data) => setServiceTypeFilter(data.optionText || 'All')}
            style={{ minWidth: '150px' }}
          >
            <Option>All</Option>
            <Option>Oil Change</Option>
            <Option>Air Filter</Option>
          </Dropdown>
        </div>

        <div className={styles.filterGroup}>
          <Dropdown
            value={deviceFilter}
            onOptionSelect={(_, data) => setDeviceFilter(data.optionText || 'All')}
            style={{ minWidth: '150px' }}
          >
            <Option>All</Option>
            <Option>Tracker</Option>
            <Option>Item</Option>
          </Dropdown>
        </div>
        <div className={styles.filterGroup}>
          <Dropdown
            value={unitsFilter}
            onOptionSelect={(_, data) => setUnitsFilter(data.optionText || 'Miles')}
            style={{ minWidth: '150px' }}
          >
            <Option>Miles</Option>
            <Option>KMs</Option>
          </Dropdown>
        </div>

        <div className={styles.filterSearch}>
          <Text className={styles.filterLabel}>Filter</Text>
          <Input
            placeholder="Tracker/Item"
            value={searchFilter}
            onChange={(e, data) => setSearchFilter(data.value)}
            style={{ minWidth: '200px' }}
          />
          <Button appearance="primary">MANAGE ITEMS</Button>
        </div>
      </div>

      {/* Table */}
      <div className={styles.tableContainer}>
        <Table>
          <TableHeader className={styles.tableHeader}>
            <TableRow className={styles.tableHeaderRow}>
              <TableHeaderCell className={styles.tableHeaderCell}>
                Tracker/Item ↓
              </TableHeaderCell>
              <TableHeaderCell className={styles.tableHeaderCell}>
                Service Type
              </TableHeaderCell>
              <TableHeaderCell className={styles.tableHeaderCell}>
                Service Interval
              </TableHeaderCell>
              <TableHeaderCell className={styles.tableHeaderCell}>
                Next Service Due
              </TableHeaderCell>
              <TableHeaderCell className={styles.tableHeaderCell}>Edit</TableHeaderCell>
              <TableHeaderCell className={styles.tableHeaderCell}>Delete</TableHeaderCell>
              <TableHeaderCell className={styles.tableHeaderCell}>Complete</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reminders.map((reminder) => (
              <TableRow key={reminder.id}>
                <TableCell>{reminder.trackerItem}</TableCell>
                <TableCell>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Wrench24Regular className={styles.serviceIcon} />
                    {reminder.serviceType}
                  </div>
                </TableCell>
                <TableCell>{reminder.serviceInterval}</TableCell>
                <TableCell>{reminder.nextServiceDue}</TableCell>
                <TableCell>
                  <Button appearance="subtle" icon={<Edit24Regular />} onClick={() => handleEdit(reminder)} />
                </TableCell>
                <TableCell>
                  <Button appearance="subtle" icon={<Delete24Regular />} />
                </TableCell>
                <TableCell>
                  <Button appearance="primary" className={styles.completeButton}>
                    COMPLETE
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Edit Service Reminder Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(_, data) => setEditDialogOpen(data.open)}>
        <DialogSurface style={{ maxWidth: '400px' }}>
          <DialogBody>
            <DialogTitle>Edit Service Reminder</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <Label htmlFor="tracker">Tracker</Label>
                  <Dropdown
                    id="tracker"
                    value={editTracker}
                    onOptionSelect={(_, data) => setEditTracker(data.optionText || '')}
                    style={{ width: '100%' }}
                  >
                    <Option>X2A-YKI BLUE MACK</Option>
                    <Option>Philly Mack Rolloff</Option>
                    <Option>Gringo FL Mack</Option>
                  </Dropdown>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <Label htmlFor="serviceType">Service Type</Label>
                    <Dropdown
                      id="serviceType"
                      value={editServiceType}
                      onOptionSelect={(_, data) => setEditServiceType(data.optionText || '')}
                      style={{ width: '100%' }}
                    >
                      <Option>Oil Change</Option>
                      <Option>Air Filter</Option>
                    </Dropdown>
                  </div>

                  <div>
                    <Label htmlFor="reminderType">Type of Reminder</Label>
                    <Dropdown
                      id="reminderType"
                      value={editReminderType}
                      onOptionSelect={(_, data) => setEditReminderType(data.optionText || '')}
                      style={{ width: '100%' }}
                    >
                      <Option>Kilometer Based</Option>
                      <Option>Time Based</Option>
                      <Option>Engine Hours Based</Option>
                    </Dropdown>
                  </div>
                </div>

                <div>
                  <Label htmlFor="currentOdometer">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      Current Odometer (km)
                      <QuestionCircle20Regular />
                    </div>
                  </Label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Input
                      id="currentOdometer"
                      value={editCurrentOdometer}
                      onChange={(e, data) => setEditCurrentOdometer(data.value)}
                      style={{ width: '100%' }}
                    />
                    <Button appearance="subtle" icon={<Edit24Regular />} />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <Label htmlFor="interval">Interval (km)</Label>
                    <Input
                      id="interval"
                      value={editInterval}
                      onChange={(e, data) => setEditInterval(data.value)}
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div>
                    <Label htmlFor="kmUntilNextDue">Kilometers Until Next Due</Label>
                    <Input
                      id="kmUntilNextDue"
                      value={editKmUntilNextDue}
                      onChange={(e, data) => setEditKmUntilNextDue(data.value)}
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="reminderTiming">Reminder Timing (how many km before?)</Label>
                  <Input
                    id="reminderTiming"
                    value={editReminderTiming}
                    onChange={(e, data) => setEditReminderTiming(data.value)}
                    style={{ width: '100%' }}
                  />
                </div>

                <div style={{ backgroundColor: '#3d3d3d', color: '#ffffff', padding: '8px 12px', marginTop: '8px' }}>
                  <Text weight="semibold">Reminder Alerts</Text>
                </div>

                <div>
                  <Label htmlFor="emailNotifications">Email Notifications</Label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Input
                      id="emailNotifications"
                      value={editEmailNotifications}
                      onChange={(e, data) => setEditEmailNotifications(data.value)}
                      style={{ width: '100%' }}
                    />
                    <Button appearance="subtle" icon={<Add20Regular />} />
                  </div>
                </div>

                <div>
                  <Label htmlFor="textNotifications">
                    Text Message Notifications <Text size={200}>(standard text rates apply)</Text>
                  </Label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Input
                      id="textNotifications"
                      value={editTextNotifications}
                      onChange={(e, data) => setEditTextNotifications(data.value)}
                      style={{ width: '100%' }}
                    />
                    <Button appearance="subtle" icon={<Add20Regular />} />
                  </div>
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setEditDialogOpen(false)}>
                CANCEL
              </Button>
              <Button appearance="primary" onClick={() => {
                console.log('Saving reminder:', {
                  tracker: editTracker,
                  serviceType: editServiceType,
                  reminderType: editReminderType,
                });
                setEditDialogOpen(false);
              }}>
                SAVE
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
